import React, { useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  Progress,
  Spinner,
  Pagination,
  Select,
  SelectItem,
} from '@heroui/react'

import { useToast } from '../../../hooks'
import {
  getMediaManagementStatus,
  batchConvertGifsToMp4,
  reencodeGifMp4,
  formatFileSize,
  formatUploadDate,
  type BatchConversionResult,
} from '../services/mediaManagementService'

// Simple icon components
const PlayIcon = () => <span>▶️</span>
const CheckIcon = () => <span>✅</span>
const ScanIcon = () => <span>🔍</span>

// Types
interface Mp4ScanState {
  isScanning: boolean
  scannedCount: number
  totalCount: number
  foundMissing: number
  canCancel: boolean
}

interface ConversionProgress {
  isRunning: boolean
  currentBatch: string[]
  results: BatchConversionResult | null
  error: string | null
}

interface Mp4Data {
  items: Array<{
    gifId: string
    fileName: string
    fileSizeBytes: number
    uploadedAt: string
  }>
  totalCount: number
  hasMore: boolean
  nextOffset: number
}

interface Mp4ManagementProps {
  onShowProgress: (type: 'conversion', progress: ConversionProgress) => void
}

export const Mp4Management: React.FC<Mp4ManagementProps> = ({
  onShowProgress,
}) => {
  const [mp4Data, setMp4Data] = useState<Mp4Data | null>(null)
  const [mp4ScanState, setMp4ScanState] = useState<Mp4ScanState>({
    isScanning: false,
    scannedCount: 0,
    totalCount: 0,
    foundMissing: 0,
    canCancel: false,
  })
  const [mp4Page, setMp4Page] = useState(1)
  const [mp4PageSize, setMp4PageSize] = useState(25)
  const [mp4SearchTerm, setMp4SearchTerm] = useState('')
  const [selectedMp4Gifs, setSelectedMp4Gifs] = useState<Set<string>>(new Set())
  const [conversionProgress, setConversionProgress] =
    useState<ConversionProgress>({
      isRunning: false,
      currentBatch: [],
      results: null,
      error: null,
    })
  const [reencodeId, setReencodeId] = useState('')
  const [isReencoding, setIsReencoding] = useState(false)

  const toast = useToast()

  // MP4 Management Functions
  const scanForMissingMp4s = async () => {
    setMp4ScanState({
      isScanning: true,
      scannedCount: 0,
      totalCount: 0,
      foundMissing: 0,
      canCancel: true,
    })

    try {
      let allMissingMp4: Array<{
        gifId: string
        fileName: string
        fileSizeBytes: number
        uploadedAt: string
      }> = []
      let offset = 0
      let hasMore = true
      let totalGifs = 0
      let scannedCount = 0

      while (hasMore) {
        const mediaStatus = await getMediaManagementStatus({
          offset,
          limit: 50,
        })

        allMissingMp4 = [...allMissingMp4, ...mediaStatus.missingMp4List]
        totalGifs = mediaStatus.totalGifs
        scannedCount += 50
        hasMore = mediaStatus.hasMore || false
        offset = mediaStatus.nextOffset || 0

        setMp4ScanState((prev) => ({
          ...prev,
          scannedCount: Math.min(scannedCount, totalGifs),
          totalCount: totalGifs,
          foundMissing: allMissingMp4.length,
        }))

        if (hasMore) {
          toast.success(
            `Scanned ${Math.min(scannedCount, totalGifs)} GIFs, found ${allMissingMp4.length} missing MP4s...`,
            { duration: 1000 }
          )
        }
      }

      setMp4Data({
        items: allMissingMp4,
        totalCount: allMissingMp4.length,
        hasMore: false,
        nextOffset: 0,
      })

      setMp4Page(1) // Reset to first page
      setSelectedMp4Gifs(new Set()) // Clear selections

      toast.success(
        `Scan complete! Found ${allMissingMp4.length} GIFs missing MP4s out of ${totalGifs} total`
      )
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to scan for missing MP4s'
      toast.error(errorMessage)
    } finally {
      setMp4ScanState((prev) => ({
        ...prev,
        isScanning: false,
        canCancel: false,
      }))
    }
  }

  // Selection handlers
  const handleSelectAllMp4 = (checked: boolean) => {
    const currentPageItems = getPaginatedMp4Items()
    if (checked) {
      const newSelected = new Set(selectedMp4Gifs)
      currentPageItems.forEach((gif) => newSelected.add(gif.gifId))
      setSelectedMp4Gifs(newSelected)
    } else {
      const newSelected = new Set(selectedMp4Gifs)
      currentPageItems.forEach((gif) => newSelected.delete(gif.gifId))
      setSelectedMp4Gifs(newSelected)
    }
  }

  const handleSelectMp4Gif = (gifId: string, checked: boolean) => {
    const newSelected = new Set(selectedMp4Gifs)
    if (checked) {
      newSelected.add(gifId)
    } else {
      newSelected.delete(gifId)
    }
    setSelectedMp4Gifs(newSelected)
  }

  // Pagination helpers
  const getFilteredMp4Items = () => {
    if (!mp4Data) return []

    return mp4Data.items.filter(
      (gif) =>
        gif.gifId.toLowerCase().includes(mp4SearchTerm.toLowerCase()) ||
        gif.fileName.toLowerCase().includes(mp4SearchTerm.toLowerCase())
    )
  }

  const getPaginatedMp4Items = () => {
    const filtered = getFilteredMp4Items()
    const startIndex = (mp4Page - 1) * mp4PageSize
    const endIndex = startIndex + mp4PageSize
    return filtered.slice(startIndex, endIndex)
  }

  const getMp4TotalPages = () => {
    const filtered = getFilteredMp4Items()
    return Math.ceil(filtered.length / mp4PageSize)
  }

  // Batch operations
  const handleBatchConvert = async () => {
    if (selectedMp4Gifs.size === 0) return

    const gifIds = Array.from(selectedMp4Gifs)
    const progress: ConversionProgress = {
      isRunning: true,
      currentBatch: gifIds,
      results: null,
      error: null,
    }

    setConversionProgress(progress)
    onShowProgress('conversion', progress)

    try {
      const result = await batchConvertGifsToMp4(gifIds)
      const updatedProgress = {
        ...progress,
        isRunning: false,
        results: result,
      }
      setConversionProgress(updatedProgress)
      onShowProgress('conversion', updatedProgress)

      // Remove successfully converted items from the list
      const successfulIds = result.results
        .filter((r) => r.success)
        .map((r) => r.gifId)
      if (mp4Data) {
        setMp4Data({
          ...mp4Data,
          items: mp4Data.items.filter(
            (item) => !successfulIds.includes(item.gifId)
          ),
          totalCount: mp4Data.totalCount - successfulIds.length,
        })
      }

      // Clear selections
      setSelectedMp4Gifs(new Set())

      toast.success(`Successfully converted ${result.successful} GIFs to MP4!`)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Conversion failed'
      const updatedProgress = {
        ...progress,
        isRunning: false,
        error: errorMessage,
      }
      setConversionProgress(updatedProgress)
      onShowProgress('conversion', updatedProgress)
      toast.error(errorMessage)
    }
  }

  const handleReencode = async () => {
    if (!reencodeId.trim()) return
    setIsReencoding(true)
    try {
      const res = await reencodeGifMp4({ gifId: reencodeId.trim() })
      toast.success(
        `Re-encoded ${res.gifId} (${res.width}x${res.height}, ${formatFileSize(
          res.fileSizeBytes
        )}) in ${res.processingTimeMs}ms`
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Re-encode failed'
      toast.error(msg)
    } finally {
      setIsReencoding(false)
    }
  }

  // Quick scan & convert all
  const handleScanAndConvertAll = async () => {
    await scanForMissingMp4s()
    // Wait a moment for state to update
    setTimeout(() => {
      if (mp4Data && mp4Data.items.length > 0) {
        setSelectedMp4Gifs(new Set(mp4Data.items.map((item) => item.gifId)))
        handleBatchConvert()
      }
    }, 1000)
  }

  // Calculate current page selection states
  const currentMp4Items = getPaginatedMp4Items()
  const allMp4Selected =
    currentMp4Items.length > 0 &&
    currentMp4Items.every((gif) => selectedMp4Gifs.has(gif.gifId))
  const someMp4Selected = currentMp4Items.some((gif) =>
    selectedMp4Gifs.has(gif.gifId)
  )

  return (
    <div className="space-y-4">
      {/* MP4 Overview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <div>
              <h3 className="text-lg font-semibold">MP4 Counterparts</h3>
              <p className="text-slate-600 dark:text-slate-400">
                Scan for GIFs missing MP4 counterparts for better messaging app
                compatibility
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onPress={scanForMissingMp4s}
                isDisabled={mp4ScanState.isScanning}
                color="primary"
                variant="flat"
                startContent={
                  mp4ScanState.isScanning ? <Spinner size="sm" /> : <ScanIcon />
                }
              >
                {mp4ScanState.isScanning
                  ? 'Scanning...'
                  : 'Scan for Missing MP4s'}
              </Button>
              <div className="flex items-center space-x-2">
                <Input
                  placeholder="GIF ID to re-encode"
                  value={reencodeId}
                  onValueChange={setReencodeId}
                  className="w-64"
                  size="sm"
                />
                <Button
                  onPress={handleReencode}
                  isDisabled={isReencoding || !reencodeId.trim()}
                  color="warning"
                >
                  {isReencoding ? 'Re-encoding…' : 'Re-encode MP4'}
                </Button>
              </div>
              {mp4Data && mp4Data.totalCount > 0 && (
                <Button
                  onPress={handleScanAndConvertAll}
                  isDisabled={
                    mp4ScanState.isScanning || conversionProgress.isRunning
                  }
                  color="success"
                  startContent={<PlayIcon />}
                >
                  Scan & Convert All
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {mp4ScanState.isScanning && (
          <CardBody>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">
                  Scanned {mp4ScanState.scannedCount} of{' '}
                  {mp4ScanState.totalCount || '?'} GIFs
                </span>
                <span className="text-sm font-medium">
                  Found {mp4ScanState.foundMissing} missing MP4s
                </span>
              </div>
              <Progress
                value={
                  mp4ScanState.totalCount > 0
                    ? (mp4ScanState.scannedCount / mp4ScanState.totalCount) *
                      100
                    : 0
                }
                className="w-full"
                color="primary"
              />
            </div>
          </CardBody>
        )}
      </Card>

      {/* MP4 Results Table */}
      {mp4Data && mp4Data.totalCount > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between w-full">
              <div>
                <h3 className="text-lg font-semibold">
                  GIFs Missing MP4 ({mp4Data.totalCount})
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Select GIFs to convert to MP4 format
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Chip variant="bordered" size="sm">
                  {selectedMp4Gifs.size} selected
                </Chip>
                <Button
                  onPress={handleBatchConvert}
                  isDisabled={
                    selectedMp4Gifs.size === 0 || conversionProgress.isRunning
                  }
                  color="success"
                  startContent={<PlayIcon />}
                >
                  Convert Selected
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            {/* Search and Page Size Controls */}
            <div className="flex items-center justify-between">
              <Input
                placeholder="Search by GIF ID or filename..."
                value={mp4SearchTerm}
                onValueChange={setMp4SearchTerm}
                className="max-w-sm"
              />
              <Select
                label="Per page"
                value={mp4PageSize.toString()}
                onChange={(e) => {
                  setMp4PageSize(Number(e.target.value))
                  setMp4Page(1) // Reset to first page
                }}
                className="w-32"
                size="sm"
              >
                <SelectItem key="25">25</SelectItem>
                <SelectItem key="50">50</SelectItem>
                <SelectItem key="100">100</SelectItem>
              </Select>
            </div>

            {/* Table */}
            <Table>
              <TableHeader>
                <TableColumn className="w-12">
                  <Checkbox
                    isSelected={allMp4Selected}
                    onValueChange={handleSelectAllMp4}
                    isIndeterminate={someMp4Selected && !allMp4Selected}
                  />
                </TableColumn>
                <TableColumn>GIF ID</TableColumn>
                <TableColumn>Filename</TableColumn>
                <TableColumn>Size</TableColumn>
                <TableColumn>Uploaded</TableColumn>
              </TableHeader>
              <TableBody>
                {currentMp4Items.map((gif) => (
                  <TableRow key={gif.gifId}>
                    <TableCell>
                      <Checkbox
                        isSelected={selectedMp4Gifs.has(gif.gifId)}
                        onValueChange={(checked) =>
                          handleSelectMp4Gif(gif.gifId, checked)
                        }
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {gif.gifId}
                    </TableCell>
                    <TableCell>{gif.fileName}</TableCell>
                    <TableCell>{formatFileSize(gif.fileSizeBytes)}</TableCell>
                    <TableCell>{formatUploadDate(gif.uploadedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {getMp4TotalPages() > 1 && (
              <div className="flex justify-center">
                <Pagination
                  total={getMp4TotalPages()}
                  page={mp4Page}
                  onChange={setMp4Page}
                  showControls
                  showShadow
                />
              </div>
            )}

            {getFilteredMp4Items().length === 0 && mp4SearchTerm && (
              <div className="text-center py-4 text-slate-600">
                No GIFs found matching &quot;{mp4SearchTerm}&quot;
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* MP4 Success State */}
      {mp4Data && mp4Data.totalCount === 0 && !mp4ScanState.isScanning && (
        <Card>
          <CardBody className="pt-6">
            <div className="text-center">
              <div className="text-6xl mb-4">
                <CheckIcon />
              </div>
              <h3 className="text-lg font-medium mb-2">
                All GIFs have MP4 counterparts!
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Every GIF in your storage has a corresponding MP4 file for
                optimal messaging app compatibility.
              </p>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default Mp4Management
